// Group rooms UI: a hub (create / random / browse / join-by-code) and the group
// room screen (a responsive video grid + group chat + invite code). ASCII only.

import { t } from '../i18n.js';
import { linkifyNodes } from './linkify.js';
import { el, ORB_SVG, toast } from '../dom.js';
import {
  RoomVisibility,
  ReportReason,
  ChatMode,
  pickRoomName,
  VIDEO_PROVIDERS,
  parseVideoUrl,
  standingRoom,
  FOCUS_MINUTES,
  ROOM_STATUS_MAX,
  OBSERVER_HELLO_TEXT, PROMPT_IDS, PROMPT_WHO } from '@lumi/shared';
import { describeRoom, langTag } from './room-counts.js';
import { createMediaControls } from './media-controls.js';
import { createTextSizeControl } from './text-size.js';
import { addSystemNotice, removeSystemNotice } from './system-notice.js';
import { createRoomVideo } from './room-video.js';
import { createActivityPanel } from './activities.js';
import { fileControls } from './chat.js';
import { createRemoteAudioSink } from '../remote-audio.js';
import { createEmojiPicker } from './emoji.js';
import { makeImageViewable } from './lightbox.js';
import {
  renderQuote,
  attachMessageActions,
  applyReaction,
  hasMyReaction,
  removeMessage,
  applyLinkPreview,
} from './message-ui.js';

const REPORT_REASONS = [
  [ReportReason.NUDITY, 'Nudity'],
  [ReportReason.HARASSMENT, 'Harassment'],
  [ReportReason.MINOR_SAFETY, 'Minor safety'],
  [ReportReason.VIOLENCE, 'Violence'],
  [ReportReason.HATE, 'Hate'],
  [ReportReason.SPAM, 'Spam'],
  [ReportReason.OTHER, 'Other'],
];

/**
 * The rooms hub: choose how to enter a group room.
 * @param {{ onCreate: Function, onRandom: Function, onJoinCode: Function,
 *           onRefresh: Function, onJoin: Function, onBack: Function }} h
 * @returns {{ root: HTMLElement, api: object }}
 */
// Room-kind icons. Inline SVG rather than an icon font or an emoji: it
// inherits currentColor, so a segment's icon changes with its selected state,
// and it keeps the source ASCII.
const ICON_VIDEO =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
  '<rect x="2" y="6" width="13" height="12" rx="2"/><path d="M15 11l6-3v8l-6-3z"/></svg>';
const ICON_AUDIO =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
  '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>';
const ICON_TEXT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
  '<path d="M21 12a8 8 0 0 1-8 8H7l-4 3v-6a8 8 0 0 1 8-8h2a8 8 0 0 1 8 3z"/></svg>';

const MODE_LABEL = {
  [ChatMode.VIDEO]: 'Video',
  [ChatMode.AUDIO]: 'Audio',
  [ChatMode.TEXT]: 'Text',
};

// Only three of them, so spell the article out rather than guessing from the
// first letter -- "a audio room" is the kind of thing that makes a product feel
// unfinished.
const MODE_ARTICLE = {
  [ChatMode.VIDEO]: 'a',
  [ChatMode.AUDIO]: 'an',
  [ChatMode.TEXT]: 'a',
};


export function renderRooms(h) {
  const nameInput = el('input', {
    type: 'text',
    id: 'roomName',
    maxlength: '40',
    placeholder: t('e.g. Late Night Lounge'),
  });
  let visibility = RoomVisibility.PUBLIC;

  // A segmented control, rather than a row of loose outlined boxes. The two
  // rows used to be independent flex rows of `flex: 1` buttons -- two segments
  // on one, three on the next -- so nothing lined up and each row read as its
  // own separate form widget. One component used twice keeps them identical.
  function segmented(label, options, initial, onPick) {
    let current = initial;
    const buttons = options.map(({ value, label: text, hint, icon }) =>
      el(
        'button',
        {
          type: 'button',
          class: 'seg',
          role: 'radio',
          aria: {
            checked: value === current ? 'true' : 'false',
            label: hint ? `${text}, ${hint}` : text,
          },
          title: hint || text,
          onClick: (e) => {
            current = value;
            buttons.forEach((b) => b.setAttribute('aria-checked', String(b === e.currentTarget)));
            onPick(value);
          },
        },
        [
          icon ? el('span', { class: 'seg-icon', html: icon }) : null,
          el('span', { class: 'seg-label', text }),
        ].filter(Boolean),
      ),
    );
    return el('div', { class: 'field' }, [
      el('span', { class: 'field-label', text: label }),
      el('div', { class: 'segmented', role: 'radiogroup', aria: { label } }, buttons),
    ]);
  }

  const visField = segmented(
    'Who can join',
    [
      { value: RoomVisibility.PUBLIC, label: 'Public', hint: 'Listed for anyone in your age group' },
      { value: RoomVisibility.PRIVATE, label: 'Private', hint: 'Hidden: only people with the code' },
    ],
    visibility,
    (v) => {
      visibility = v;
    },
  );

  // What KIND of room. Every room used to be a video room whether or not anyone
  // wanted one, so a group that meant to type was still asked for a camera.
  // Whatever they picked on the start screen carries in here: choosing Text
  // there and then being handed a video room is the system forgetting something
  // the user just told it.
  let roomMode = h.initialMode || ChatMode.VIDEO;

  const randomBtn = el('button', {
    class: 'btn btn-primary btn-lg btn-block',
    onClick: () => h.onRandom(roomMode),
  });
  function labelRandom() {
    // The kind is IN the button, not in a footnote reading "uses the kind
    // selected on the left" -- which stopped being true the moment the cards
    // stacked on a phone, and read as an apology for the layout either way.
    randomBtn.textContent = `Drop me in ${MODE_ARTICLE[roomMode]} ${MODE_LABEL[roomMode].toLowerCase()} room`;
  }
  labelRandom();

  const modeField = segmented(
    'Kind of room',
    [
      { value: ChatMode.VIDEO, label: 'Video', hint: 'Cameras on: a grid of faces', icon: ICON_VIDEO },
      { value: ChatMode.AUDIO, label: 'Audio', hint: 'Voices only, no cameras', icon: ICON_AUDIO },
      { value: ChatMode.TEXT, label: 'Text', hint: 'Typing only: no camera or microphone', icon: ICON_TEXT },
    ],
    roomMode,
    (v) => {
      roomMode = v;
      labelRandom();
    },
  );

  const codeInput = el('input', {
    type: 'text',
    id: 'roomCode',
    maxlength: '6',
    placeholder: 'ABC123',
    class: 'code-input',
  });

  const list = el('div', { class: 'room-list' });
  // Names currently in use, so Generate offers something free -- the same
  // courtesy the server does when it names a random room. Only what this client
  // can see, which is the public directory for its own band; the server accepts
  // whatever is typed either way, so this is politeness rather than a rule.
  let knownNames = [];

  // The rooms the site keeps open, above the public list. They arrive in the
  // same ROOM_LIST, marked `standing`, and are split out here so the ordinary
  // list stays a list of rooms people made. The section is not there at all
  // when the server sends none (the feature is off), rather than an empty box.
  const standingList = el('div', { class: 'room-list standing-list' });
  const standingSection = el('section', { class: 'room-card browse standing-rooms', hidden: '' }, [
    el('div', { class: 'card-head' }, [
      el('span', { class: 'eyebrow', text: t('Always open') }),
      el('h2', { text: t('Rooms that are always open') }),
    ]),
    el('p', {
      class: 'field-help',
      text: t('Nobody owns these and they never close. Walk in, listen for a while, and say something when you feel like it.'),
    }),
    standingList,
  ]);

  function setStandingRooms(rooms) {
    if (!rooms.length) {
      standingSection.hidden = true;
      standingList.replaceChildren();
      return;
    }
    standingList.replaceChildren(
      ...rooms.map((r) => {
        const entry = standingRoom(r.standing);
        const tag = langTag(r.lang);
        return el('div', { class: 'room-row' }, [
          el('div', { class: 'room-row-main' }, [
            el('div', { class: 'room-name' }, [
              el('span', { class: 'room-name-text', text: r.name }),
              el('span', { class: `room-mode mode-${r.mode || ChatMode.VIDEO}`, text: MODE_LABEL[r.mode] || 'Video' }),
              tag ? el('span', { class: 'room-lang', title: t('The language this room is for'), text: tag }) : null,
            ].filter(Boolean)),
            // "3 here, 1 watching", or "Quiet right now". Never "0 people": an
            // open room with nobody in it is an invitation, not a failure.
            el('div', { class: `room-meta${Number(r.members) > 0 ? '' : ' room-quiet'}`, text: describeRoom(r) }),
            entry && entry.blurb ? el('div', { class: 'room-blurb', text: entry.blurb }) : null,
          ].filter(Boolean)),
          el('button', { class: 'btn btn-sm', text: t('Join'), onClick: () => h.onJoin({ roomId: r.id }) }),
        ]);
      }),
    );
    standingSection.hidden = false;
  }

  function setRooms(all) {
    const standing = (all || []).filter((r) => r && r.standing);
    const rooms = (all || []).filter((r) => r && !r.standing);
    setStandingRooms(standing);
    knownNames = (all || []).map((r) => r.name).filter(Boolean);
    if (!rooms || !rooms.length) {
      // A designed empty state. "No public rooms yet. Create one!" as a line of
      // grey text under a heading is the moment the screen looks unfinished --
      // and it is the FIRST thing most people see here, since the list is
      // usually empty early on.
      list.replaceChildren(
        el('div', { class: 'rooms-empty' }, [
          el('span', { class: 'rooms-empty-orb', html: ORB_SVG }),
          el('p', { class: 'rooms-empty-title', text: t('No public rooms right now') }),
          el('p', {
            class: 'rooms-empty-sub',
            // No "on the left": the cards stack on a phone, which is exactly
            // the bug in the note this screen used to carry.
            text: t('Create one and it will show up here for everyone in your age group.'),
          }),
        ]),
      );
      return;
    }
    list.replaceChildren(
      ...rooms.map((r) =>
        el('div', { class: 'room-row' }, [
          el('div', { class: 'room-row-main' }, [
            el('div', { class: 'room-name' }, [
              el('span', { class: 'room-name-text', text: r.name }),
              // The kind, in the listing: you should know what you are walking
              // into before your camera is asked for.
              el('span', {
                class: `room-mode mode-${r.mode || ChatMode.VIDEO}`,
                text: MODE_LABEL[r.mode] || 'Video',
              }),
            ]),
            el('div', {
              // An empty room is not a failed room. It is somewhere that
              // exists to be come back to, and saying "0 people here" makes it
              // read as broken rather than as an invitation. (`dormant`, not
              // `quiet`: `quiet` on this same row means the room is THE quiet
              // room, which is a different thing entirely.)
              class: `room-meta${r.dormant ? ' room-quiet' : ''}`,
              text: r.dormant
                ? 'Quiet right now: be the first one back'
                : `${r.members} ${Number(r.members) === 1 ? 'person' : 'people'} here`,
            }),
          ]),
          el('button', {
            class: 'btn btn-sm',
            text: r.dormant ? 'Reopen' : 'Join',
            onClick: () => h.onJoin({ roomId: r.id }),
          }),
        ]),
      ),
    );
  }
  setRooms(null); // the empty state, rather than a bare "Loading rooms..."

  // ---- Staff drop-in --------------------------------------------------
  // Every live room, both bands, private ones included -- the only way to
  // reach a private room without its code. Shown ONLY to verified staff, and
  // the server refuses the request from anyone else regardless of what the
  // client renders.
  const staffList = el('div', { class: 'room-list' });
  // Visible or invisible. Visible is the default and the honest one -- staff
  // crossing band segregation is defensible because the room can see it happen
  // -- so invisible is a deliberate choice, made before you walk in.
  let dropInInvisible = false;
  const VISIBLE_NOTE =
    'Visible: you join as a participant and everyone sees your LUMI STAFF badge. Every drop-in is logged.';
  const INVISIBLE_NOTE =
    'Invisible: the room is not told you joined and you are not in their list. Other staff can still see you. '
    + 'The moment you say anything (a message, an image, even a reaction) you are announced with your LUMI STAFF badge. '
    + 'Invisible is for watching, not for talking. Logged either way.';
  // Declared before the control, which reads it from its click handler.
  const staffNote = el('p', { class: 'field-help staff-note', text: VISIBLE_NOTE });
  const dropInField = segmented(
    'Drop in as',
    [
      { value: 'visible', label: 'Visible', hint: 'You appear in the room wearing the LUMI STAFF badge' },
      { value: 'invisible', label: 'Invisible', hint: 'Nobody is told you joined, until you say something, which announces you with your badge' },
    ],
    'visible',
    (v) => {
      dropInInvisible = v === 'invisible';
      staffNote.textContent = dropInInvisible ? INVISIBLE_NOTE : VISIBLE_NOTE;
    },
  );
  function setAllRooms(rooms) {
    if (!rooms || !rooms.length) {
      staffList.replaceChildren(el('div', { class: 'muted', text: t('No live rooms right now.') }));
      return;
    }
    staffList.replaceChildren(
      ...rooms.map((r) =>
        el('div', { class: 'room-row' }, [
          el('div', { class: 'room-row-main' }, [
            el('div', { class: 'room-name' }, [
              el('span', { class: 'room-name-text', text: r.name || 'Room' }),
              el('span', { class: `room-mode mode-${r.mode || ChatMode.VIDEO}`, text: MODE_LABEL[r.mode] || 'Video' }),
              // Say plainly what is being entered. A minors' room is the one
              // an adult account is normally never in, so it should never be
              // possible to walk into one without having read that word.
              el('span', { class: `room-band band-${r.band}`, text: r.band === 'minor' ? 'MINORS' : 'Adults' }),
              r.visibility === RoomVisibility.PRIVATE
                ? el('span', { class: 'room-private', text: t('Private') })
                : null,
            ].filter(Boolean)),
            el('div', {
              class: 'room-meta',
              text: `${r.members}/${r.capacity} here`,
            }),
          ]),
          el('button', {
            class: 'btn btn-sm',
            text: t('Drop in'),
            title: t('Join as Lumi Staff: you will be visible to the room, wearing the staff badge'),
            onClick: () => h.onJoin({ roomId: r.id, invisible: dropInInvisible }),
          }),
        ]),
      ),
    );
  }
  setAllRooms(null);

  // A slot for "Your community", filled by app.js when the person has one
  // (or may start one). The hub knows nothing about communities; it only
  // keeps the place, above the rooms the site keeps open.
  const communitySlot = el('div', { class: 'community-slot' });

  const staffPanel = h.isStaff
    ? el('section', { class: 'room-card staff-panel' }, [
        el('div', { class: 'browse-head' }, [
          el('div', { class: 'card-head' }, [
            el('span', { class: 'eyebrow eyebrow-staff', text: t('Lumi Staff') }),
            el('h2', { text: t('Every live room') }),
          ]),
          el('button', {
            class: 'btn btn-ghost btn-sm',
            text: t('Refresh'),
            onClick: () => h.onRefreshAll?.(),
          }),
        ]),
        el('p', {
          class: 'field-help staff-intro',
          text: t('You can enter any room here, including private ones and the minors band.'),
        }),
        dropInField,
        staffNote,
        staffList,
      ])
    : null;
  if (staffPanel) h.onRefreshAll?.();

  const root = el('div', { class: 'landing' }, [
    el('div', { class: 'landing-inner rooms-hub' }, [
      el('div', { class: 'topbar' }, [
        el('button', { class: 'btn btn-ghost btn-sm', text: t('<- Back'), onClick: () => h.onBack() }),
        el('span', { class: 'spacer' }),
        el('span', { html: ORB_SVG }),
        'Group rooms',
      ]),

      el('header', { class: 'hub-head' }, [
        el('h1', { class: 'hub-title', text: t('Group rooms') }),
        el('p', {
          class: 'hub-sub',
          text: t('Talk with a few people at once. Start your own, or drop into one that is already going.'),
        }),
      ]),

      el('div', { class: 'rooms-grid' }, [
        el('section', { class: 'room-card room-card-create' }, [
          el('div', { class: 'card-head' }, [
            el('span', { class: 'eyebrow', text: t('Create') }),
            el('h2', { text: t('Start a room') }),
          ]),
          el('div', { class: 'field' }, [
            el('div', { class: 'field-label-row' }, [
              el('label', { class: 'field-label', for: 'roomName', text: t('Room name') }),
              el('button', {
                type: 'button',
                class: 'btn btn-ghost btn-xs name-gen',
                text: t('Generate'),
                title: t('Suggest a name nobody is using'),
                onClick: () => {
                  nameInput.value = pickRoomName(knownNames);
                  nameInput.focus();
                },
              }),
            ]),
            nameInput,
          ]),
          visField,
          modeField,
          el('button', {
            class: 'btn btn-primary btn-lg btn-block',
            text: t('Create room'),
            onClick: () => h.onCreate({ name: nameInput.value, visibility, mode: roomMode }),
          }),
        ]),

        el('section', { class: 'room-card room-card-join' }, [
          el('div', { class: 'card-head' }, [
            el('span', { class: 'eyebrow', text: t('Jump in') }),
            el('h2', { text: t('Straight into one') }),
          ]),
          randomBtn,
          el('p', { class: 'field-help', text: t('We will find a room of that kind with space in it.') }),
          el('div', { class: 'or' }, [el('span', { text: 'or' })]),
          el('div', { class: 'field' }, [
            el('label', { class: 'field-label', for: 'roomCode', text: t('Have a code?') }),
            el('div', { class: 'code-join' }, [
              codeInput,
              el('button', {
                class: 'btn',
                text: t('Join'),
                onClick: () => {
                  const code = codeInput.value.trim().toUpperCase();
                  if (code.length < 4) return toast('Enter the room code.');
                  return h.onJoinCode(code);
                },
              }),
            ]),
          ]),
        ]),
      ]),

      staffPanel,

      communitySlot,

      standingSection,

      el('section', { class: 'room-card browse' }, [
        el('div', { class: 'browse-head' }, [
          el('div', { class: 'card-head' }, [
            el('span', { class: 'eyebrow', text: t('Browse') }),
            el('h2', { text: t('Public rooms') }),
          ]),
          el('button', { class: 'btn btn-ghost btn-sm', text: t('Refresh'), onClick: () => h.onRefresh() }),
        ]),
        list,
      ]),
    ]),
  ]);

  h.onRefresh();
  return { root, api: { setRooms, setAllRooms, communitySlot } };
}

/**
 * The group room screen: a video grid + chat + invite code.
 * @param {{ room: object, onSend: Function, onLeave: Function, onShare: Function }} h
 * @returns {{ root: HTMLElement, api: object }}
 */
export function renderGroupChat(h) {
  // The kind of room this is. Read first: the roster and the layout both
  // branch on it well before the header is built.
  const mode = h.room.mode || ChatMode.VIDEO;
  const grid = el('div', { class: 'video-grid' });
  // A wrapper that exists only to be a container-query container. The grid
  // cannot query itself -- @container styles descendants, never the element
  // that declares the container -- and .group-stage is the wrong shape to ask,
  // because it also holds the header, the banners and the controls.
  const gridWrap = el('div', { class: 'video-grid-wrap' }, [grid]);
  // Whoever is here by voice and text, not camera: a strip of small cards
  // under the grid. Membership is wide and cameras are metered, so most of a
  // big room lives here. Each card is still a tile -- name, badges, Go 1:1,
  // Mute, Report -- just not a camera-shaped one.
  const audioStrip = el('div', { class: 'audio-strip hidden' });
  let videoHolders = null; // who the server says is on camera; null until it has said
  /** @type {Map<string, HTMLElement>} peerId -> tile */
  const tiles = new Map();
  // Per-member state the server relays: a one-line status (quiet rooms) and
  // whether they have not yet spoken. 'self' is you. Declared here because the
  // tile and the roster both read them.
  /** @type {Map<string, string>} peerId -> status line */
  const peerStatus = new Map();
  /** @type {Set<string>} peerIds still quietly listening */
  const observingPeers = new Set();
  let speakerVol = 1; // applies to every remote tile (not your own muted self-view)
  // Speaker volume that works on desktop AND iOS (where media-element volume is read-only).
  const remoteAudio = createRemoteAudioSink();
  let selfStream = null; // the local self-view stream (for mic mute + cam toggle)

  // A small "Report" button + reason menu on each remote tile.
  function reportButton(peerId) {
    const menu = el(
      'div',
      { class: 'report-menu hidden' },
      REPORT_REASONS.map(([value, lbl]) =>
        el('button', {
          class: 'report-reason',
          text: lbl,
          onClick: () => {
            menu.classList.add('hidden');
            h.onReport?.(peerId, value);
            toast('Reported. Thanks for looking out.');
          },
        }),
      ),
    );
    const btn = el('button', {
      class: 'tile-report',
      title: t('Report this person'),
      text: t('Report'),
      onClick: () => menu.classList.toggle('hidden'),
    });
    return el('div', { class: 'tile-report-wrap' }, [btn, menu]);
  }

  function tile(id, label) {
    const video = el('video', { autoplay: '', playsinline: '', muted: id === 'self' ? '' : undefined });
    // Name and indicators sit in ONE row along the bottom. They used to be
    // pinned to separate corners, which put the staff badge underneath the
    // per-tile action buttons; in a row they simply line up, however many a
    // person has.
    // The status span sits after the name: a quiet room shows "reading" or
    // "essay due tomorrow" next to each person. Empty everywhere else.
    const children = [
      video,
      el('div', { class: 'tile-tags' }, [
        el('span', { class: 'tile-label', text: label }),
        el('span', { class: 'tile-status', text: peerStatus.get(id) || '' }),
      ]),
    ];
    if (id !== 'self') {
      children.push(reportButton(id));
      children.push(
        el('div', { class: 'tile-actions' }, [
          el('button', {
            class: 'tile-act',
            title: t('Ask to chat one-on-one'),
            text: t('Go 1:1'),
            onClick: () => {
              h.onGoPrivate?.(id);
              toast('Asked to go private. Waiting for them to accept...');
            },
          }),
          el('button', {
            class: 'tile-act tile-mute-btn',
            title: t('Hide this person: no video, audio, texts, or files from them, and they cannot ask you to go 1:1'),
            text: t('Mute'),
            onClick: () => h.onToggleMute?.(id),
          }),
          h.canModerate
            ? el('button', {
                class: 'tile-act tile-act-danger',
                title: t('Remove this person for 15 minutes (moderator)'),
                text: t('Remove'),
                onClick: () => kickMember(id),
              })
            : null,
        ].filter(Boolean)),
      );
    }
    // data-peer lets a report snapshot find THIS member's video (see grabPeerFrame).
    const node = el('div', { class: 'tile', 'data-peer': String(id) }, children);
    node._id = String(id);
    // On a phone with a video pinned the tile is a 6.5 x 5rem filmstrip cell,
    // and the buttons covered the face. There they stay hidden until the tile
    // is tapped (styles.css, the last phone block). A desktop tile shows them
    // regardless, so this class changes nothing there.
    node.addEventListener('click', (e) => {
      if (e.target.closest('button, .report-menu')) return;
      node.classList.toggle('tile-open');
    });
    node._video = video;
    if (id !== 'self') video.volume = speakerVol;
    tiles.set(id, node);
    placeTile(node);
    return node;
  }

  // Where a tile lives: the grid if this member is on camera, else the strip.
  // The server says who holds a camera slot; until it has said anything (an
  // older server never does), a tile with video counts as on camera.
  function placeTile(node) {
    const onCamera = videoHolders ? videoHolders.has(node._id) : !node.classList.contains('tile-novideo');
    const want = onCamera ? grid : audioStrip;
    if (node.parentNode !== want) want.append(node);
    audioStrip.classList.toggle('hidden', audioStrip.childElementCount === 0);
  }
  function setVideoSlots(slots) {
    if (!slots || mode !== ChatMode.VIDEO) return;
    videoHolders = new Set(slots.holders || []);
    slotsPill.textContent = `On camera ${slots.used}/${slots.max}`;
    slotsPill.classList.toggle('slots-full', slots.used >= slots.max);
    slotsPill.classList.remove('hidden');
    for (const node of tiles.values()) placeTile(node);
  }
  // Camera on, every slot taken: say so on your own card, where you will look.
  function setSelfVideoWaiting(on) {
    tiles.get('self')?.classList.toggle('tile-waiting', !!on);
  }

  // Moderator kick: a reason is required (the citation), a note is optional.
  function kickMember(peerId) {
    const choice = window.prompt(
      'Remove this person for 15 minutes.\nYou must cite a reason: type the number:\n' +
        REPORT_REASONS.map(([, label], i) => `${i + 1}. ${label}`).join('\n'),
    );
    if (choice == null) return;
    const idx = Number(choice) - 1;
    if (!Number.isInteger(idx) || !REPORT_REASONS[idx]) return;
    const note = window.prompt('Add a short note for the admin (optional):') || '';
    h.onKick?.(peerId, REPORT_REASONS[idx][0], note.slice(0, 500));
  }

  // Self-media controls -- the SAME shared component as 1:1 chat, so mic mute/volume,
  // speaker volume, noise suppression, background blur, and filters behave identically.
  // Speaker volume here applies to every remote tile (and any that join later).
  const media = createMediaControls({
    canVideo: true,
    getLocalStream: () => selfStream,
    onEnableMic: () => h.onEnableMic?.(),
    onEnableCam: () => h.onEnableCam?.(),
    onMicVolume: (g) => h.onMicVolume?.(g),
    onSpeakerVolume: (v) => {
      speakerVol = v;
      remoteAudio.setVolume(v); // governs every remote tile, element-volume or Web Audio
    },
    onEffect: (kind, value) => h.onEffect?.(kind, value),
    onCamToggle: (on) => {
      const self = tiles.get('self');
      if (self && self._video) self._video.classList.toggle('cam-off', !on);
      h.onCamToggle?.(on); // in a group room: close or claim the camera slot
    },
  });
  const { micBtn, camBtn, settingsBtn, settingsPanel } = media;
  const controls = el('div', { class: 'controls group-media-controls' }, [micBtn, camBtn, settingsBtn]);

  // ---- Going private: the room-privacy vote and 1:1 request prompts ----
  const isPrivate = h.room.visibility === 'private';
  const visPill = el('span', { class: `pill ${isPrivate ? '' : 'hidden'}`, text: t('Private') });
  // How many of the camera slots are taken. Shown once the server has said.
  const slotsPill = el('span', { class: 'pill slots-pill hidden', title: t('People on camera, out of the camera slots this room has') });
  // No vote in a room the site keeps open: it stays open to everyone in the
  // band, and the server refuses the vote regardless.
  const voteBtn = el('button', {
    class: `btn btn-ghost btn-sm ${isPrivate || h.room.standing ? 'hidden' : ''}`,
    text: t('Vote: go private'),
    onClick: () => h.onVoteStart?.(),
  });

  const voteText = el('span', { class: 'vote-text' });
  const voteTally = el('span', { class: 'vote-tally' });
  const lockVote = () => {
    voteYes.disabled = true;
    voteNo.disabled = true;
  };
  const voteYes = el('button', { class: 'btn btn-primary btn-sm', text: t('Yes'), onClick: () => { h.onVoteCast?.(true); lockVote(); } });
  const voteNo = el('button', { class: 'btn btn-ghost btn-sm', text: 'No', onClick: () => { h.onVoteCast?.(false); lockVote(); } });
  const voteBanner = el('div', { class: 'vote-banner hidden' }, [voteText, voteTally, voteYes, voteNo]);
  function showVote(data) {
    voteText.textContent = data && data.byHandle ? `${data.byHandle} wants to make this room private.` : 'Make this room private?';
    voteTally.textContent = ` (${(data && data.yes) || 0}/${(data && data.total) || 0} yes)`;
    voteYes.disabled = false;
    voteNo.disabled = false;
    voteBanner.classList.remove('hidden');
  }
  function updateVote(data) {
    voteTally.textContent = ` (${(data && data.yes) || 0}/${(data && data.total) || 0} yes)`;
  }
  function endVote(result) {
    voteBanner.classList.add('hidden');
    if (result && result.passed) {
      voteBtn.classList.add('hidden');
      visPill.classList.remove('hidden');
    }
  }

  let privateFromId = null;
  const privateText = el('span');
  const privatePrompt = el('div', { class: 'private-prompt hidden' }, [
    privateText,
    el('button', { class: 'btn btn-primary btn-sm', text: t('Accept'), onClick: () => { h.onPrivateRespond?.(privateFromId, true); privatePrompt.classList.add('hidden'); } }),
    el('button', { class: 'btn btn-ghost btn-sm', text: t('Decline'), onClick: () => { h.onPrivateRespond?.(privateFromId, false); privatePrompt.classList.add('hidden'); } }),
  ]);
  function askPrivate(fromHandle, fromId) {
    privateFromId = fromId;
    privateText.textContent = `${fromHandle} wants to chat one-on-one. `;
    privatePrompt.classList.remove('hidden');
  }

  function setSelf(stream) {
    const node = tiles.get('self') || tile('self', 'You');
    node._video.srcObject = stream;
    selfStream = stream;
    media.api.setLocalStream(stream); // mic/cam buttons + reveal the settings popover
    applyTags('self'); // your own badges, if they were set before this tile existed
  }
  // Show the PROCESSED (filtered/blurred) video in your own tile so effects are visible,
  // keeping the original audio track so mic-mute still works.
  function setSelfVideo(track) {
    const node = tiles.get('self');
    if (!node) return;
    const cur = node._video.srcObject;
    const audio = cur ? cur.getAudioTracks() : [];
    const s = new MediaStream([...(track ? [track] : []), ...audio]);
    node._video.srcObject = s;
    selfStream = s;
  }
  function addPeer(peerId, stream, handle) {
    const label = handle || `Guest ${String(peerId).slice(0, 4)}`;
    const node = tiles.get(peerId) || tile(peerId, label);
    const labelEl = node.querySelector('.tile-label');
    if (labelEl) labelEl.textContent = label;
    node._video.srcObject = stream;
    node._video.play().catch(() => {}); // dynamically-created tiles do not always autoplay audio
    if (stream) remoteAudio.attach(peerId, stream, node._video); // speaker volume (desktop + iOS)
    if (stream) node.classList.remove('tile-novideo');
    placeTile(node);
  }
  // Ensure a tile exists for a member even before (or without) any media, so the
  // per-tile actions -- including Mute -- are available for audio/text-only members.
  function ensurePeer(peerId, handle) {
    if (!peerId || tiles.get(peerId)) {
      if (handle && tiles.get(peerId)) tiles.get(peerId).querySelector('.tile-label').textContent = handle;
      refreshRoster();
      return;
    }
    const node = tile(peerId, handle || `Guest ${String(peerId).slice(0, 4)}`);
    node.classList.add('tile-novideo');
    placeTile(node);
    refreshRoster();
  }
  function removePeer(peerId) {
    setTyping(peerId, null, false); // they cannot still be typing
    remoteAudio.detach(peerId);
    peerFlags.delete(peerId);
    const node = tiles.get(peerId);
    if (node) {
      node.remove();
      tiles.delete(peerId);
    }
    audioStrip.classList.toggle('hidden', audioStrip.childElementCount === 0);
    refreshRoster();
  }
  // Mute/unmute a peer for ME: hide their video (overlay) and silence their audio.
  // (Their texts and files are filtered upstream in app.js.) The state rides on the
  // persistent tile, so it survives stream updates.
  function setMuted(peerId, muted) {
    const node = tiles.get(peerId);
    if (!node) return;
    node.classList.toggle('tile-muted', muted);
    remoteAudio.setMuted(peerId, muted); // silence this peer on element OR Web Audio path
    const btn = node.querySelector('.tile-mute-btn');
    if (btn) btn.textContent = muted ? 'Unmute' : 'Mute';
    refreshRoster();
  }

  // Verified-moderator badge on a member's tile (unfakeable -- driven by the
  // account-gated moderator flag, never the handle).
  // What each peer carries. The tile is drawn from the WHOLE picture rather
  // than by each flag toggling its own chip, because one badge now depends on
  // another and independent setters would render whichever arrived last.
  /** @type {Map<string, { staff: boolean, moderator: boolean, plus: boolean }>} */
  const peerFlags = new Map();

  function setFlag(peerId, key, on) {
    const f = peerFlags.get(peerId) || { staff: false, moderator: false, plus: false };
    f[key] = !!on;
    peerFlags.set(peerId, f);
    applyTags(peerId);
  }

  // Verified moderator: admin-granted, carried in the session token, so it can
  // never be typed into a handle.
  function setModerator(peerId, isMod) {
    setFlag(peerId, 'moderator', isMod);
  }

  // Verified LUMI STAFF. Granted by an administrator and carried in the session
  // token, so it cannot be typed into a handle or a room name.
  function setStaff(peerId, isStaff) {
    setFlag(peerId, 'staff', isStaff);
  }

  // The subtle Plus mark a Plus member is promised. Deliberately the quietest
  // of the badges: it says "supporting member", not "authority", and it must
  // never read as something that affects matching or moderation.
  function setPlus(peerId, isPlus) {
    setFlag(peerId, 'plus', isPlus);
  }

  // ---- Who is in the room -------------------------------------------------
  // Badges and names used to live ONLY on the video tiles, so a text room --
  // which hides the grid entirely -- showed neither. You could not see who you
  // were talking to, and a verified LUMI STAFF badge silently stopped existing
  // in exactly the room where "who is this person" matters most and there is no
  // face to go on. The roster is built from the same tiles and flags, so it is
  // right in every mode rather than being a second source of truth.
  const rosterList = el('div', { class: 'roster-list' });
  const rosterCount = el('span', { class: 'roster-count' });
  let rosterOpen = mode !== ChatMode.VIDEO; // in a video room the faces already say it

  function memberName(id, node) {
    const label = node.querySelector('.tile-label');
    const name = label ? label.textContent.trim() : '';
    return name || (id === 'self' ? 'You' : `Guest ${String(id).slice(0, 4)}`);
  }

  /**
   * Alone in a room, with the one thing worth doing about it.
   *
   * A room with one person in it is the shape the whole site is fighting: it
   * looks broken, and the person who opened it is the only one who can fix
   * it. So say so, once, with the link already copied to hand -- and say what
   * the room is FOR, because "invite someone" is an instruction and "put on
   * a video and watch it together" is a reason.
   *
   * Once per room, and only after a moment: firing it the instant somebody
   * opens a room they have just created is telling them nobody came to a
   * party that has not started. It goes away the moment anybody arrives, and
   * comes back if the room empties again -- which is exactly when it is
   * useful a second time.
   */
  const ALONE_PROMPT_AFTER_MS = 20_000;
  let alonePromptTimer = null;
  let alonePromptShown = false;
  const ALONE_NOTICE_ID = 'room-alone';

  function inviteLinkForRoom() {
    return `${location.origin}/${h.room?.community ? 'c' : 'r'}/${h.room?.code || ''}`;
  }

  function showAlonePrompt() {
    if (alonePromptShown || memberTotal() > 1) return;
    alonePromptShown = true;
    const link = inviteLinkForRoom();
    addSystem(
      'You are the only one here. Post the Lumi Link for this room anywhere you like. Whoever opens it lands straight in this room, and you can watch a video, put music on, or just talk.',
      {
        id: ALONE_NOTICE_ID,
        action: {
          text: 'Copy Lumi Link',
          title: 'Copy the link to this room, ready to paste anywhere',
          onClick: () => {
            navigator.clipboard?.writeText(link).then(
              () => toast('Room link copied. Paste it anywhere.'),
              () => toast(link),
            );
          },
        },
      },
    );
  }

  /** Everyone here, you included -- the same count the roster shows. */
  function memberTotal() {
    return 1 + [...tiles.keys()].filter((id) => id !== 'self').length;
  }

  function reviewAloneness() {
    const alone = memberTotal() <= 1;
    if (!alone) {
      clearTimeout(alonePromptTimer);
      alonePromptTimer = null;
      return;
    }
    if (alonePromptShown || alonePromptTimer) return;
    alonePromptTimer = setTimeout(() => {
      alonePromptTimer = null;
      showAlonePrompt();
    }, ALONE_PROMPT_AFTER_MS);
  }

  function refreshRoster() {
    // Self first, then everyone else in join order. "self" is listed whether or
    // not it has a TILE: setSelf only runs when there is a local stream, and a
    // text room never opens one -- so without this the one person guaranteed to
    // be in the room, you, was the one missing from the list.
    const ids = ['self', ...[...tiles.keys()].filter((id) => id !== 'self')];
    rosterCount.textContent = String(ids.length);
    // The roster already runs on every arrival and departure, so it is the
    // honest place to notice that somebody is on their own.
    reviewAloneness();
    // Somebody arriving may be the fourth person a game was waiting for, and
    // somebody leaving may take that away again. Cheap: the panel redraws only
    // while the picker is open and nothing is running.
    activityPanel.api.refreshPicker();
    rosterList.replaceChildren(
      ...ids.map((id) => {
        const node = tiles.get(id);
        const f = peerFlags.get(id) || {};
        const name = node ? memberName(id, node) : 'You';
        const chips = [];
        if (f.staff) chips.push(el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') }));
        if (f.moderator && !f.staff) chips.push(el('span', { class: 'mod-badge', title: t('Verified moderator'), text: t('MOD') }));
        if (f.plus && !f.staff) chips.push(el('span', { class: 'plus-mark', title: t('Lumi Plus member'), text: 'PLUS' }));
        // Earned by hosting rooms people came to, and never taken away for
        // being absent. Deliberately not a score and deliberately not
        // ranked against anybody.
        if (f.regular) chips.push(el('span', { class: 'regular-mark', title: t('Has hosted rooms people came to'), text: 'REGULAR' }));
        if (id === ownerPeerId || (id === 'self' && isRoomOwner)) {
          // Who can pin a video, share control, and stop it. Without this the
          // owner's box simply is not there for everyone else, with nothing on
          // screen to say why.
          chips.push(el('span', { class: 'owner-mark', title: t('Started this room'), text: 'HOST' }));
        }
        if (node && node.classList.contains('tile-muted')) {
          chips.push(el('span', { class: 'roster-muted', title: t('You muted this person'), text: 'MUTED' }));
        }
        // Quietly listening. Not a mark against anyone -- lurking is allowed,
        // and said so -- just the honest state of somebody who has not spoken.
        if (observingPeers.has(id)) {
          chips.push(el('span', { class: 'roster-observing', title: t('Has not said anything yet'), text: 'listening' }));
        }
        const status = peerStatus.get(id);
        return el('div', { class: `roster-row${id === 'self' ? ' is-self' : ''}` }, [
          el('span', { class: 'roster-name', text: name }),
          status ? el('span', { class: 'roster-status', text: status }) : null,
          ...chips,
        ].filter(Boolean));
      }),
    );
    rosterList.hidden = !rosterOpen;
  }

  const rosterToggle = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm roster-toggle',
    aria: { expanded: String(rosterOpen) },
    title: t('Who is in this room'),
    onClick: () => {
      rosterOpen = !rosterOpen;
      rosterToggle.setAttribute('aria-expanded', String(rosterOpen));
      rosterList.hidden = !rosterOpen;
    },
  }, [rosterCount, el('span', { text: t(' here') })]);

  // Rebuild the chips in a fixed order, so the row reads the same whichever
  // order the flags arrived in.
  function applyTags(peerId) {
    const node = tiles.get(peerId);
    const row = node && node.querySelector('.tile-tags');
    if (!row) return;
    row.querySelectorAll('.staff-badge, .mod-badge, .plus-mark').forEach((n) => n.remove());
    const f = peerFlags.get(peerId) || {};
    if (f.staff) {
      row.append(el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') }));
    }
    // Staff ARE moderators, so "LUMI STAFF MOD" says the same thing twice and
    // makes the staff badge look like one chip among several. Same reasoning as
    // PLUS below: the one signal that says this really is the Lumi team works
    // best standing on its own.
    if (f.moderator && !f.staff) {
      row.append(el('span', { class: 'mod-badge', title: t('Verified moderator'), text: t('MOD') }));
    }
    // Staff carry Plus perks too, and the same rule applies.
    if (f.plus && !f.staff) {
      row.append(el('span', { class: 'plus-mark', title: t('Lumi Plus member'), text: 'PLUS' }));
    }

    // The roster carries the same badges, and in a text room it is the ONLY
    // place they appear.
    refreshRoster();
  }

  // Live captions over the grid: a member's transcribed speech, shown briefly.
  const captionBar = el('div', { class: 'caption-bar' });
  let capTimer = null;
  function showCaption(handle, text) {
    captionBar.textContent = handle ? `${handle}: ${text}` : text;
    captionBar.classList.add('show');
    clearTimeout(capTimer);
    capTimer = setTimeout(() => captionBar.classList.remove('show'), 5000);
  }

  const messages = el('div', { class: 'messages' });
  // The sender's name and, crucially, their badges. In a video room a badge
  // rides on the speaker's tile; in a TEXT room there is no tile and no face,
  // so without this a verified LUMI STAFF member is indistinguishable from
  // anyone who typed the same name into the handle box -- which is exactly
  // backwards, since text is where impersonation is easiest and identity is
  // hardest to check.
  /** peerId -> the name the room knows them by, for the reaction tooltip. */
  // The fallback needs a DOUBLE backslash: in source, '\$&' is just '$&', and
  // '$&' in a replacement means "the text that matched" -- so this used to
  // replace every character with itself and escape nothing at all.
  const cssEscapeId = (v) =>
    (window.CSS && CSS.escape ? CSS.escape(String(v)) : String(v).replace(/[^\w-]/g, '\\$&'));

  function nameOfPeer(peerId) {
    if (!peerId || peerId === 'me') return null;
    const node = tiles.get(peerId);
    const label = node && node.querySelector('.tile-label');
    const name = label ? label.textContent.trim() : '';
    // No tile means the room was never told about them -- someone who has left,
    // or an invisible staff member. Either way this is not the place to say so.
    return name || null;
  }

  function senderLabel(from, peerId, explicit) {
    // Prefer the flags carried ON the message. The roster lookup only knows
    // about people the room was told had joined, which an invisible staff
    // member is not -- and their message must still show the badge, because
    // what the badge guarantees is that a message speaking with Lumi's
    // authority visibly does so. Whether they are still here is a separate
    // fact, and a deliberately withheld one.
    const f = explicit && (explicit.staff || explicit.moderator || explicit.plus)
      ? explicit
      : (peerId && peerFlags.get(peerId)) || {};
    const chips = [];
    if (f.staff) chips.push(el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') }));
    if (f.moderator && !f.staff) chips.push(el('span', { class: 'mod-badge', title: t('Verified moderator'), text: t('MOD') }));
    if (f.plus && !f.staff) chips.push(el('span', { class: 'plus-mark', title: t('Lumi Plus member'), text: 'PLUS' }));
    return el('span', { class: 'who' }, [
      // Names were cut to FOUR characters here, so "Homer" read as "Home" and
      // two people whose names shared a prefix were the same person on screen.
      // The peer-id fallback is already shortened by the caller.
      el('span', { class: 'who-name', text: String(from).slice(0, 24) }),
      ...chips,
    ]);
  }

  // Pinned system notices live ABOVE the transcript, so one that scrolls away
  // in a busy room is still readable.
  const sysPins = el('div', { class: 'sys-pins' });

  // The shared video. Only ever built for an ADULT room -- a minors room has no
  // video panel at all, and the server refuses to pin one regardless.
  let isRoomOwner = false;
  let ownerPeerId = h.room.ownerId || null;
  // A room the site keeps open has no owner, so the owner-only controls (the
  // stage, the vote, watch-together) are not built at all rather than built
  // and hidden. The server refuses them regardless.
  const isStanding = !!h.room.standing;
  const roomVideo =
    h.room.band === 'adult' && !isStanding
      ? createRoomVideo({
          onControl: (action, atMs) => h.onVideoControl?.(action, atMs),
          onPin: (url) => h.onVideoPin?.(url),
          onShare: (on) => h.onVideoShare?.(on),
          // The queue (contract D7): add, remove, vote to skip, take the next
          // turn. The panel keeps no rules of its own -- the server decides
          // who may do what and answers with the whole queue either way.
          onQueue: (action, extra) => h.onVideoQueue?.(action, extra),
        })
      : null;

  // Something to do between the talking (contract D0). One activity at a time,
  // in a sandboxed frame that can see only what this panel hands it. The
  // catalog filters itself by the room's band and kind, so a text room is
  // never offered a game that needs cameras.
  const activityPanel = createActivityPanel({
    band: h.room.band,
    mode,
    standing: isStanding,
    selfId: h.selfId || '',
    handle: h.handle || null,
    nameOf: (peerId) => nameOfPeer(peerId),
    // Everyone here, self included -- counted exactly the way the roster
    // counts. `tiles` holds a card per PEER and a 'self' tile only once local
    // media exists, so in a text room it is empty and you are not in it: using
    // its size told the picker nobody was here and greyed out every game, even
    // the ones that need one person.
    memberCount: () => 1 + [...tiles.keys()].filter((id) => id !== 'self').length,
    onOp: (op, slug, data) => h.onActivity?.(op, slug, data),
  });

  // ---- Who is typing --------------------------------------------------
  // A group room had no indicator at all. Naming people matters more here than
  // in a 1:1: "somebody is typing" in a room of six tells you nothing worth
  // knowing, and two people composing at once is exactly when you want to wait.
  const typingBar = el('div', { class: 'typing' });
  /** @type {Map<string, { handle: string, timer: number }>} */
  const typers = new Map();

  // ---- Unread ---------------------------------------------------------
  // Scroll up to read something and the conversation carries on without you,
  // with no way back but dragging. A divider marks where you left off, and a
  // button carries you to the end with a count of what you missed.
  const jumpBtn = el('button', {
    class: 'btn btn-sm jump-latest hidden',
    type: 'button',
    onClick: () => {
      messages.scrollTop = messages.scrollHeight;
      // Cleared HERE rather than left to the scroll handler. When the column
      // is not actually scrollable, setting scrollTop fires no scroll event,
      // so the button did nothing at all: it wiggled and the marker stayed.
      clearUnread();
    },
  });
  let unread = 0;

  /**
   * Can the person see the end of the conversation?
   *
   * A column with nothing to scroll is one where you are ALWAYS at the
   * bottom, and a column that has not been measured yet (clientHeight 0,
   * which happens while the panel above it is resizing) is not evidence of
   * anything. Both used to read as "scrolled away", which is how a message
   * you had just sent, and could plainly see, announced itself as unread.
   */
  const atBottom = () => {
    if (!messages.clientHeight) return true;
    if (messages.scrollHeight <= messages.clientHeight) return true;
    return messages.scrollHeight - messages.scrollTop - messages.clientHeight < 60;
  };

  function clearUnread() {
    unread = 0;
    jumpBtn.classList.add('hidden');
    messages.querySelector('.unread-line')?.remove();
  }

  function noteIncoming() {
    unread += 1;
    if (unread === 1) {
      // The line goes in ONCE, where you stopped reading, and stays put as
      // more arrives beneath it.
      messages.append(el('div', { class: 'unread-line' }, [el('span', { text: t('New messages') })]));
    }
    jumpBtn.textContent = `${unread} new message${unread === 1 ? '' : 's'}, jump to latest`;
    jumpBtn.classList.remove('hidden');
  }

  messages.addEventListener(
    'scroll',
    () => {
      if (atBottom()) clearUnread();
    },
    { passive: true },
  );

  function drawTyping() {
    const names = [...typers.values()].map((who) => who.handle);
    if (!names.length) {
      typingBar.textContent = '';
      return;
    }
    if (names.length === 1) typingBar.textContent = `${names[0]} is typing...`;
    else if (names.length === 2) typingBar.textContent = `${names[0]} and ${names[1]} are typing...`;
    else typingBar.textContent = `${names[0]} and ${names.length - 1} others are typing...`;
  }

  function setTyping(peerId, handle, state) {
    if (!peerId) return;
    const existing = typers.get(peerId);
    if (existing) clearTimeout(existing.timer);
    if (!state) {
      typers.delete(peerId);
      drawTyping();
      return;
    }
    typers.set(peerId, {
      handle: handle || nameOfPeer(peerId) || 'Someone',
      // Clear it ourselves if the "stopped" never arrives -- a dropped
      // connection would otherwise leave somebody typing forever.
      timer: setTimeout(() => {
        typers.delete(peerId);
        drawTyping();
      }, 5000),
    });
    drawTyping();
  }

  /** A notice from Lumi. The only path that renders the system style. */
  function addSystem(text, opts = {}) {
    return addSystemNotice(messages, sysPins, text, opts);
  }

  // Any link a registered provider recognises. Built from the registry so a
  // new site is offered here the moment it is added, with nothing to update.
  const PROVIDER_HOSTS = [...new Set(VIDEO_PROVIDERS.flatMap((p) => p.hosts))]
    .sort((a, b) => b.length - a.length)
    .map((hostname) => hostname.replace(/[.]/g, '\\.'));
  const MEDIA_RE = new RegExp(`https?://(?:${PROVIDER_HOSTS.join('|')})/[^\\s<>"']*`, 'i');

  /**
   * Offer to pin a YouTube link somebody posted.
   *
   * Hung off the LINK rather than off a preview card: a card only appears when
   * the fetch succeeded and found a title, so a perfectly good video whose page
   * did not cooperate offered nothing at all -- and the pin box in the panel
   * above was not obvious enough to find on its own.
   */
  function offerWatchTogether(node, text) {
    if (!roomVideo || !isRoomOwner) return;
    const m = String(text || '').match(MEDIA_RE);
    if (m && !parseVideoUrl(m[0])) return; // a link to the site, not to a video
    if (!m || node.querySelector('.watch-together')) return;
    node.append(
      el('button', {
        class: 'btn btn-sm watch-together',
        text: t('Watch together'),
        title: t('Pin this for everyone in the room'),
        onClick: () => h.onVideoPin?.(m[0]),
      }),
    );
  }

  function addMessage(from, text, opts = {}) {
    const mine = from === 'me';
    // Deliberately no "system" case. This used to be `from === 'system'`, so
    // anyone whose handle was "system" had their chat lines rendered as
    // official notices. System notices are built by addSystem() from a server
    // message a client cannot send; nothing typed reaches that path.
    const system = false;
    const node = el('div', { class: `msg ${mine ? 'me' : 'them'}${system ? ' system' : ''}` }, [
      renderQuote(opts.replyTo),
      !mine && !system ? senderLabel(from, opts.peerId, opts.flags) : null,
      // Links are clickable: a Lumi Link always, a link off the site only in
      // an adult room. Built as elements from split text, never as markup.
      el('span', { class: 'msg-text' }, linkifyNodes(text, { external: h.room.band === 'adult' })),
    ].filter(Boolean));
    // A system notice is nobody's message: it cannot be replied to, reacted to,
    // or unsent, so it gets no chrome.
    if (!system && opts.id) {
      attachMessageActions(node, {
        id: opts.id,
        mine,
        text,
        who: mine ? 'You' : String(from).slice(0, 24),
        onReply: startReply,
        onReact: toggleReaction,
        onDelete: mine ? deleteOwn : null,
      });
    }
    offerWatchTogether(node, text);
    appendAndFollow(node, mine);
  }

  /**
   * Put a message on screen, and decide whether to follow it down.
   *
   * Somebody reading further up is not yanked back: the message is marked
   * unread instead. But YOUR OWN message always follows, because you just
   * wrote it and you are obviously looking at it -- marking it unread put a
   * "New messages" line and a "1 new message" pill above a message the
   * person was already reading, and neither would clear.
   */
  function appendAndFollow(node, mine) {
    const following = mine || atBottom();
    messages.append(node);
    if (following) {
      messages.scrollTop = messages.scrollHeight;
      clearUnread();
      return;
    }
    noteIncoming();
  }

  // ---- Reply, react, unsend --------------------------------------------------
  let replyTo = null; // the message being answered, or null
  const replyBar = el('div', { class: 'reply-bar hidden' });
  function startReply(quote) {
    replyTo = quote;
    replyBar.replaceChildren(
      el('span', { class: 'reply-label', text: `Replying to ${quote.who}: ` }),
      el('span', { class: 'reply-text', text: quote.text.slice(0, 140) }),
      el('button', { class: 'reply-cancel', title: t('Cancel reply'), text: 'x', onClick: cancelReply }),
    );
    replyBar.classList.remove('hidden');
    input.focus();
  }
  function cancelReply() {
    replyTo = null;
    replyBar.classList.add('hidden');
    replyBar.replaceChildren();
  }
  function toggleReaction(id, emoji) {
    const on = !hasMyReaction(messages, id, emoji);
    applyReaction(messages, id, emoji, 'me', on, nameOfPeer); // show it at once, do not wait for the round trip
    h.onReact?.(id, emoji, on);
    setSelfObserving(false); // a reaction counts as a first word
  }
  function deleteOwn(id) {
     
    if (!window.confirm('Delete this message for everyone in the room?')) return;
    removeMessage(messages, id);
    h.onUnsend?.(id);
  }

  function addFile(from, { mime, url, name }, opts = {}) {
    const isImg = /^image\//.test(mime || '');
    const mine = from === 'me';
    const system = false; // see addMessage: a shared file is never a notice either
    const content = isImg
      ? el('img', { src: url, alt: name || 'image', class: 'file-img' })
      : el('a', { href: url, download: name || 'file', text: `Download ${name || 'file'}` });
    if (isImg) makeImageViewable(content, messages);
    const wrap = el('div', { class: 'file-msg' }, [content]);
    // Same as 1:1: a received file can be removed from your own screen, and
    // reported WITH the file attached (see ui/chat.js fileControls).
    if (isImg && !mine && !system) {
      fileControls({
        media: content,
        mime,
        wrap,
        onReport: (reason, image) => h.onReport?.(from, reason, image),
      });
    }
    const node = el('div', { class: `msg ${mine ? 'me' : 'them'}` }, [
      !mine && !system ? senderLabel(from, opts.peerId) : null,
      wrap,
    ].filter(Boolean));
    // A shared image is a message like any other. It used to be built straight
    // into the list with no id and no actions, so it could not be replied to,
    // reacted to, or taken back -- the three things you can do to a line of
    // text sitting right above it.
    if (!system && opts.id) {
      attachMessageActions(node, {
        id: opts.id,
        mine,
        text: name || 'image',
        who: mine ? 'You' : String(from).slice(0, 24),
        onReply: startReply,
        onReact: toggleReaction,
        onDelete: mine ? deleteOwn : null,
      });
    }
    appendAndFollow(node, mine);
  }

  // Images only -- the server refuses anything else, so do not offer it here.
  const fileInput = el('input', { type: 'file', accept: 'image/*', title: t('Send an image') });
  fileInput.style.display = 'none';
  fileInput.addEventListener('change', () => {
    const f = fileInput.files && fileInput.files[0];
    if (f) {
      h.onFile?.(f);
      setSelfObserving(false); // sharing something is not quietly listening
    }
    fileInput.value = '';
  });
  const attachBtn = el('button', { class: 'btn btn-ghost btn-sm', text: '+', title: t('Share a file'), onClick: () => fileInput.click() });

  const input = el('input', { type: 'text', placeholder: t('Message the room...'), maxlength: '4000' });
  // Tell the room you are typing. The group composer never did -- the receive
  // side and the indicator were both built, and nothing ever SENT one, so
  // nobody could possibly have seen it. Stops on its own after a pause, and on
  // send, so a half-written message you abandoned does not leave you typing
  // forever.
  let typingTimer = null;
  input.addEventListener('input', () => {
    h.onTyping?.(true);
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => h.onTyping?.(false), 1500);
  });
  const emoji = createEmojiPicker(input);
  const send = () => {
    const text = input.value.trim();
    if (!text) return;
    const quote = replyTo;
    const id = h.onSend(text, quote); // the caller mints the id and relays it
    addMessage('me', text, { id, replyTo: quote });
    setSelfObserving(false); // you have spoken
    // Answered it, so stop asking. A prompt that stays after you have replied
    // to it is furniture, and this one sits directly above the conversation.
    if (quote && quote.id === PROMPT_IDS.question) dismissQuestion();
    clearTimeout(typingTimer);
    h.onTyping?.(false); // sending is the end of typing
    input.value = '';
    cancelReply();
    emoji.close();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') send();
  });

  // Ask somebody waiting whether they would like to come in for a few
  // minutes. Only offered to the host of an adult room -- the server
  // enforces both, this just avoids showing a button that would be refused.
  const stageBtn = el('button', {
    class: 'btn btn-ghost btn-sm hidden',
    text: t('Invite someone in'),
    title: t('Ask one person who is waiting if they would like to join for ten minutes'),
    onClick: () => h.onStageRequest?.(),
  });

  // Put this room on the front page. The host of an adult room that is not
  // one of the site's own and not a community's; the server checks every
  // part of that again, this only avoids a button that would be refused.
  const promoteBtn = el('button', {
    class: 'btn btn-ghost btn-sm hidden',
    text: t('Promote'),
    title: t('Put this room on the front page for a day, a weekend, a week, or a month'),
    onClick: () => h.onPromote?.(),
  });

  // Who is here as a guest, and for how long. Shown to everyone: a room
  // should be able to see that somebody is a visitor rather than a member,
  // and the guest should be able to see their own clock.
  const stageBanner = el('div', { class: 'stage-banner hidden' });
  let stageUntil = 0;
  let stageWho = '';
  let stageIsMe = false;

  function refreshStageBanner() {
    if (!stageUntil) {
      stageBanner.classList.add('hidden');
      return;
    }
    const left = Math.max(0, stageUntil - Date.now());
    const mm = Math.floor(left / 60000);
    const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
    stageBanner.classList.remove('hidden');
    stageBanner.textContent = stageIsMe
      ? `You are a guest here for ${mm}:${ss}. Leave whenever you like.`
      : `${stageWho} is visiting for ${mm}:${ss}.`;
  }
  setInterval(refreshStageBanner, 1000);

  // ---- The pinned card: rules, and the question of the hour ---------------
  // Every room has a rules card, pinned above the chat and posted as the first
  // system line (so it is in the record even once the card is collapsed). The
  // PNAS field experiment: pinning the rules raised newcomer participation by
  // seventy percent. Collapsing is remembered per room slug, so somebody who
  // has read the quiet room's card is not shown it open every visit.
  const RULES_KEY = 'lumi.rulesCollapsed';
  const rulesSlug = h.room.standing || 'default';
  function readCollapsed() {
    try {
      const all = JSON.parse(localStorage.getItem(RULES_KEY) || '{}') || {};
      return !!all[rulesSlug];
    } catch {
      return false;
    }
  }
  function writeCollapsed(on) {
    try {
      const all = JSON.parse(localStorage.getItem(RULES_KEY) || '{}') || {};
      all[rulesSlug] = !!on;
      localStorage.setItem(RULES_KEY, JSON.stringify(all));
    } catch {
      /* not remembered, but applied for this room */
    }
  }
  let rules = Array.isArray(h.room.rules) ? h.room.rules.slice() : [];
  const rulesList = el('ul', { class: 'rules-list' });
  const pinQuestion = el('p', { class: 'pin-question' });
  // Answering the question of the hour in a busy room used to be a message
  // like any other, so twenty people answering read as twenty unrelated
  // remarks. Replying to the question quotes it above the answer, which is
  // the whole difference between a room talking past itself and a room having
  // a conversation. Hidden until there IS a question.
  const questionReply = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-xs pin-question-reply hidden',
    text: t('Answer this'),
    title: t('Answer the question of the hour, quoting it so everyone can see what you are answering'),
    onClick: () => {
      if (!currentQuestion) return;
      startReply({ id: PROMPT_IDS.question, text: currentQuestion, who: PROMPT_WHO.question });
    },
  });
  const rulesToggle = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-xs',
    aria: { expanded: 'true' },
    onClick: () => setRulesCollapsed(!rulesCard.classList.contains('collapsed')),
  });
  const rulesCard = el('div', { class: 'rules-card' }, [
    el('div', { class: 'rules-head' }, [el('span', { class: 'rules-title', text: t('Room rules') }), rulesToggle]),
    rulesList,
  ]);
  // Its OWN card, not a line inside the rules.
  //
  // It used to live in the rules card, and under a heading reading ROOM RULES
  // that says the question IS a rule -- which it is not. It is the opposite
  // kind of thing: the rules are the standing terms of the room and barely
  // change, while this is a live prompt that turns over every hour and asks
  // to be answered. Collapsing the rules used to take the question with it
  // too, which is how the Answer button came to be invisible to anybody who
  // had tidied the card away.
  // Sent away by hand, or automatically once it has been answered. A prompt
  // that stays after you have replied to it is just furniture, and this one
  // sits directly above the conversation it is asking about.
  let questionDismissed = false;
  function dismissQuestion() {
    questionDismissed = true;
    questionCard.hidden = true;
  }
  const questionClose = el('button', {
    class: 'sys-close question-close',
    type: 'button',
    title: t('Dismiss this question'),
    aria: { label: 'Dismiss this question' },
    text: '\u00d7',
    onClick: dismissQuestion,
  });
  // The eyebrow folds the card, the way the rules card's Show and Hide does.
  // Measured at 390x844 it was 121px of question above a 40px transcript:
  // the right feature in the wrong place once the panel is short. Folding
  // is not dismissing, which is the other button: this one comes back.
  const questionEyebrow = el('button', {
    class: 'question-eyebrow',
    type: 'button',
    text: t('Question of the hour'),
    title: t('Fold this away, or open it again'),
    aria: { expanded: 'true' },
    onClick: () => {
      const folded = questionCard.classList.toggle('collapsed');
      questionEyebrow.setAttribute('aria-expanded', String(!folded));
    },
  });
  const questionCard = el('div', { class: 'question-card', hidden: true }, [
    questionEyebrow,
    questionClose,
    el('div', { class: 'pin-question-row' }, [pinQuestion, questionReply]),
  ]);
  function setRulesCollapsed(on) {
    rulesCard.classList.toggle('collapsed', on);
    rulesToggle.textContent = on ? 'Show' : 'Hide';
    rulesToggle.setAttribute('aria-expanded', String(!on));
    writeCollapsed(on);
  }
  function drawRules() {
    rulesList.replaceChildren(...rules.map((r) => el('li', { text: String(r) })));
    rulesCard.hidden = rules.length === 0;
  }
  drawRules();
  rulesCard.classList.toggle('collapsed', readCollapsed());
  rulesToggle.textContent = rulesCard.classList.contains('collapsed') ? 'Show' : 'Hide';
  rulesToggle.setAttribute('aria-expanded', String(!rulesCard.classList.contains('collapsed')));

  /** The pinned card arrived, on join or on the hour. */
  let currentQuestion = null;
  function setPin(pin) {
    if (!pin) return;
    if (Array.isArray(pin.rules) && pin.rules.length) {
      rules = pin.rules.slice();
      drawRules();
    }
    const q = typeof pin.question === 'string' && pin.question.trim() ? pin.question.trim() : null;
    if (q !== currentQuestion) {
      currentQuestion = q;
      pinQuestion.textContent = q ? `Question of the hour: ${q}` : '';
      questionReply.classList.toggle('hidden', !q);
      // A NEW question is a new thing to be asked, so a dismissal does not
      // carry across the hour.
      questionDismissed = false;
      // The card goes with the question: no question, no empty card.
      questionCard.hidden = !q;
    }
  }
  setPin(h.room.pin);

  // ---- Observer mode ----------------------------------------------------------
  // "N watching", and while YOU are one, a single tap that says hello. Hidden
  // once you have spoken: the button's only job is to make the first line
  // easy, and after the first line there is nothing left for it to do.
  let watching = Number(h.room.observers) || 0;
  let selfObserving = true; // everyone arrives quietly listening
  const watchingText = el('span', { class: 'watching-text' });
  const helloBtn = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-xs',
    text: t('Say hi, I have been lurking'),
    title: `Posts "${OBSERVER_HELLO_TEXT}" as a message from you`,
    onClick: () => {
      const id = h.onObserverHello?.(); // the caller mints the id and sends it
      addMessage('me', OBSERVER_HELLO_TEXT, { id });
      setSelfObserving(false);
    },
  });
  const observerNote = el('div', { class: 'observer-note' }, [watchingText, helloBtn]);
  function drawObservers() {
    watchingText.textContent = watching > 0 ? `${watching} watching` : '';
    helloBtn.classList.toggle('hidden', !selfObserving);
    observerNote.classList.toggle('hidden', watching <= 0 && !selfObserving);
  }
  function setWatching(n) {
    watching = Math.max(0, Number(n) || 0);
    drawObservers();
  }
  function setSelfObserving(on) {
    selfObserving = !!on;
    if (on) observingPeers.add('self');
    else observingPeers.delete('self');
    drawObservers();
    refreshRoster();
  }
  function setPeerObserving(peerId, on) {
    if (!peerId) return;
    if (on) observingPeers.add(peerId);
    else observingPeers.delete(peerId);
    refreshRoster();
  }
  observingPeers.add('self');
  drawObservers();

  // ---- The quiet room ---------------------------------------------------------
  // Mics start muted and cameras off (app.js does that half), a shared timer
  // anyone may start when none is running, and a one-line status next to each
  // name. Both controls exist only in a quiet room.
  const isQuiet = !!h.room.quiet;
  let focusUntil = 0;
  let focusMinutes = 0;
  const focusPill = el('span', { class: 'focus-pill hidden' });
  const focusStarts = FOCUS_MINUTES.map((mins) =>
    el('button', {
      type: 'button',
      class: 'btn btn-sm',
      text: `Start ${mins} min`,
      title: t('A shared timer everyone in the room can see'),
      onClick: () => h.onFocusStart?.(mins),
    }),
  );
  const focusLabel = el('span', { text: t('Sit down to work with a shared timer:') });
  const focusBar = el('div', { class: `focus-bar${isQuiet ? '' : ' hidden'}` }, [focusLabel, ...focusStarts, focusPill]);
  function refreshFocus() {
    if (!isQuiet) return;
    if (!focusUntil) {
      focusPill.classList.add('hidden');
      focusLabel.textContent = 'Sit down to work with a shared timer:';
      focusStarts.forEach((b) => b.classList.remove('hidden'));
      return;
    }
    const left = Math.max(0, focusUntil - Date.now());
    const mm = Math.floor(left / 60000);
    const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
    focusPill.textContent = `${mm}:${ss} left of ${focusMinutes} min`;
    focusPill.classList.remove('hidden');
    focusLabel.textContent = 'Focus time.';
    focusStarts.forEach((b) => b.classList.add('hidden'));
  }
  function setFocus(state) {
    focusUntil = state && state.until ? Number(state.until) || 0 : 0;
    focusMinutes = state && state.minutes ? Number(state.minutes) || 0 : 0;
    refreshFocus();
  }
  if (isQuiet) setInterval(refreshFocus, 1000);

  const statusInput = el('input', {
    type: 'text',
    maxlength: String(ROOM_STATUS_MAX),
    placeholder: t('What are you doing?'),
    'aria-label': 'What are you doing? Shown next to your name',
  });
  // Sent on Enter or when the field loses focus, so nobody has to find a
  // Save button for a line this small.
  let lastSentStatus = '';
  const sendStatus = () => {
    const text = statusInput.value.trim().slice(0, ROOM_STATUS_MAX);
    if (text === lastSentStatus) return;
    lastSentStatus = text;
    h.onStatus?.(text);
  };
  statusInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      sendStatus();
      statusInput.blur();
    }
  });
  statusInput.addEventListener('blur', sendStatus);
  const statusField = el('div', { class: `status-field${isQuiet ? '' : ' hidden'}` }, [
    el('span', { class: 'field-label', text: t('Your status') }),
    statusInput,
  ]);
  /** A member's status changed (the server relays yours back too). */
  function setPeerStatus(peerId, text) {
    if (!peerId) return;
    const clean = String(text || '').trim();
    if (clean) peerStatus.set(peerId, clean);
    else peerStatus.delete(peerId);
    const node = tiles.get(peerId);
    const span = node && node.querySelector('.tile-status');
    if (span) span.textContent = clean;
    if (peerId === 'self') {
      lastSentStatus = clean;
      if (statusInput.value.trim() !== clean) statusInput.value = clean;
    }
    refreshRoster();
  }
  for (const mem of h.room.members || []) {
    if (mem && mem.peerId) {
      if (mem.observing) observingPeers.add(mem.peerId);
      if (mem.status) peerStatus.set(mem.peerId, String(mem.status));
    }
  }

  // The language tag in the header, when the room is about one.
  const roomLang = langTag(h.room.lang);

  const code = h.room.code || '';
  // A community's room says so in the header, and its invite link is the
  // /c/ landing (ask to join), never the /r/ one (walk in). The panel with
  // the members and the sessions is app.js's, in the slot below the header.
  const isCommunity = !!h.room.community;
  /**
   * The header's actions: two that are always on the bar, and five behind
   * More when the screen is narrow.
   *
   * Seven peer chips in a wrap-only bar with no priority meant the only
   * thing wrapping could do was add rows, and each row came out of the
   * stage: 167px of header on a portrait tablet, three rows of buttons.
   *
   * ONE set of nodes serves both layouts. On a wide screen `.group-more` is
   * `display: contents`, so the group dissolves and its children sit in the
   * header row exactly as they always did; under the threshold it becomes a
   * popover and the More button appears. Nothing is moved in the DOM, so a
   * button that hides itself (Promote, Invite) keeps working in both.
   */
  const moreBtn = el('button', {
    class: 'btn btn-ghost btn-sm group-more-btn',
    type: 'button',
    text: t('More'),
    title: t('The rest of this room\u2019s controls'),
    aria: { expanded: 'false', haspopup: 'true' },
    onClick: () => setMoreOpen(!groupActions.classList.contains('more-open')),
  });
  const codePill = el('span', { class: 'room-code-pill', title: t('Room code'), text: code });
  const copyLinkBtn = el('button', {
    class: 'btn btn-ghost btn-sm',
    text: t('Copy Lumi Link'),
    title: t('Copy the Lumi Link for this room'),
    onClick: () => {
      const link = `${location.origin}/${isCommunity ? 'c' : 'r'}/${code}`;
      navigator.clipboard?.writeText(link).then(
        () => toast(isCommunity ? 'Lumi Link copied. Whoever opens it can ask to join; the host decides.' : 'Lumi Link copied. Anyone can verify and join the room.'),
        () => toast(`Share this code to invite: ${code}`),
      );
    },
  });
  const moreGroup = el('div', { class: 'group-more' }, [stageBtn, promoteBtn, voteBtn, codePill, copyLinkBtn]);
  const groupActions = el('div', { class: 'group-actions' }, [
    // Shown to everyone: the picker itself says who may start something,
    // which is a kinder answer than a button that is simply not there.
    el('button', {
      class: 'btn btn-ghost btn-sm',
      type: 'button',
      text: t('Activities'),
      title: t('Something to do together'),
      onClick: () => {
        setMoreOpen(false);
        activityPanel.api.showPicker();
        // With a video on, the picker opens BELOW the video panel, in a
        // stage that scrolls. Bring it into view, or the tap looks like
        // it did nothing.
        if (!activityPanel.root.classList.contains('hidden')) {
          activityPanel.root.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        }
      },
    }),
    moreBtn,
    moreGroup,
    el('button', { class: 'btn btn-danger btn-sm', text: t('Leave'), onClick: () => h.onLeave() }),
  ]);

  /**
   * Open or close the More popover, and pin it where it can never be cut off.
   *
   * It used to be positioned INSIDE the header, which meant every ancestor got
   * a vote: the stage becomes a scroll container while a video panel is up
   * (see .group-stage:has(.room-video...) in styles.css), and a menu that
   * hangs below a header inside a scrolling box gets clipped by it. The
   * operator opened it in his own room and the second item was sliced in half.
   *
   * Fixed to the viewport and measured off the button instead. Nothing above
   * it in the tree can clip it, no stacking context can bury it, and it cannot
   * hang off the side of a narrow window. The CSS keeps the absolute rules as
   * the fallback for a browser mid-layout; these inline values win.
   */
  function setMoreOpen(open) {
    groupActions.classList.toggle('more-open', !!open);
    moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) {
      moreGroup.style.cssText = '';
      return;
    }
    const r = moreBtn.getBoundingClientRect();
    const GAP = 6;
    const EDGE = 8;
    moreGroup.style.position = 'fixed';
    moreGroup.style.zIndex = '120';
    moreGroup.style.top = `${Math.round(r.bottom + GAP)}px`;
    // Right-aligned to the button, then pulled back inside the window rather
    // than allowed to hang off it.
    moreGroup.style.left = 'auto';
    moreGroup.style.right = `${Math.max(EDGE, Math.round(window.innerWidth - r.right))}px`;
    // Never taller than what is left below the button: a long menu scrolls
    // itself instead of running off the bottom of the screen.
    moreGroup.style.maxHeight = `${Math.max(120, Math.round(window.innerHeight - r.bottom - GAP - EDGE))}px`;
    moreGroup.style.overflowY = 'auto';
  }
  // A window that changes shape under an open menu leaves it pointing at
  // nothing, so it is re-measured rather than left behind.
  const remeasureMore = () => {
    if (groupActions.classList.contains('more-open')) setMoreOpen(true);
  };
  window.addEventListener('resize', remeasureMore);
  window.addEventListener('scroll', remeasureMore, true);
  // Anything chosen from the popover closes it, and so does Escape or a tap
  // anywhere else: a menu that stays open over the room is a menu in the way.
  moreGroup.addEventListener('click', (e) => {
    if (e.target.closest('button')) setMoreOpen(false);
  });
  // Named, so leaving the room can take them off again (see api.dispose).
  const onMoreKey = (e) => {
    if (e.key === 'Escape') setMoreOpen(false);
  };
  const onMoreClickAway = (e) => {
    if (!groupActions.contains(e.target)) setMoreOpen(false);
  };
  document.addEventListener('keydown', onMoreKey);
  document.addEventListener('click', onMoreClickAway);

  const roomTitle = el('span', { class: 'room-title', text: h.room.name || 'Room' });
  const communitySlot = el('div', { class: 'community-slot' });
  const root = el('div', { class: `group-screen room-mode-${mode}` }, [
    el('div', { class: 'group-stage' }, [
      el('div', { class: 'group-head' }, [
        el('div', {}, [
          roomTitle,
          el('span', { class: `room-mode mode-${mode}`, text: mode === ChatMode.TEXT ? 'Text' : mode === ChatMode.AUDIO ? 'Audio' : 'Video' }),
          roomLang ? el('span', { class: 'room-lang', title: t('The language this room is for'), text: roomLang }) : null,
          isCommunity ? el('span', { class: 'pill community-pill', title: t('A community: a private room its host keeps open for invited members'), text: t('Community') }) : null,
          visPill,
          slotsPill,
        ].filter(Boolean)),
        groupActions,
      ]),
      stageBanner,
      focusBar,
      voteBanner,
      privatePrompt,
      roomVideo ? roomVideo.root : null,
      // Inside the stage, as a sibling of the grid: the stylesheet shrinks the
      // camera tiles to a filmstrip while an activity is running
      // (.group-stage:has(.activity-panel.is-running) .video-grid-wrap), which
      // only works if the two are in the same box.
      activityPanel.root,
      gridWrap,
      audioStrip,
      captionBar,
      settingsPanel,
      controls,
    ]),
    el('div', { class: 'group-chat' }, [
      el('div', { class: 'chat-head' }, [
        el('span', { class: 'chat-head-title', text: t('Room chat') }),
        el('div', { class: 'chat-head-tools' }, [rosterToggle, createTextSizeControl()]),
      ]),
      // Everything above the transcript that is NOT the transcript: who is
      // here, the community's panel, the rules, the question of the hour,
      // and Lumi's pinned notices. One scroll region, so a room with all of
      // them open cannot push the composer off the bottom of a phone. They
      // used to be five separate flex children that could not shrink below
      // their content, and the overflow left through the bottom of the
      // column: measured at 360x640 with a whiteboard running, the composer
      // sat 413px below the fold.
      el('div', { class: 'chat-notes' }, [rosterList, communitySlot, rulesCard, questionCard, sysPins]),
      messages,
      jumpBtn,
      typingBar,
      replyBar,
      emoji.panel,
      observerNote,
      statusField,
      el('div', { class: 'composer' }, [
        attachBtn,
        fileInput,
        emoji.button,
        input,
        el('button', { class: 'btn btn-primary btn-sm', text: t('Send'), onClick: send }),
      ]),
    ]),
  ]);

  // Draw it once up front: the room already knows its members from the join
  // payload, so the roster should not be empty until somebody moves.
  refreshRoster();
  // The rules and the question live in the CARD above, which is always on
  // screen and collapses to a "Show" button rather than going away. Posting
  // them into the transcript as well meant walking into a room and reading the
  // same rules three times: the card, a Lumi message repeating all of them, and
  // another repeating the question. The card is the one place they belong.

  return {
    root,
    api: {
      setPin: (pin) => {
        const before = currentQuestion;
        setPin(pin);
        // A NEW question on the hour is worth a line -- but only when the card
        // is collapsed. Expanded, the card already shows it, and a message
        // saying the same thing an inch below it is the duplication again.
        if (currentQuestion && currentQuestion !== before && rulesCard.classList.contains('collapsed')) {
          // Answerable right here. The card carries the same button, but a
          // collapsed card hides it, and this line is what most people see.
          addSystem(`Question of the hour: ${currentQuestion}`, {
            action: {
              text: 'Answer this',
              title: 'Answer the question of the hour, quoting it so everyone can see what you are answering',
              onClick: () => {
                if (currentQuestion) {
                  startReply({ id: PROMPT_IDS.question, text: currentQuestion, who: PROMPT_WHO.question });
                }
              },
            },
          });
        }
      },
      setWatching,
      setSelfObserving,
      setPeerObserving,
      setFocus,
      setPeerStatus,
      setSelf,
      setSelfVideo,
      setVideoSlots,
      setSelfVideoWaiting,
      addPeer,
      ensurePeer,
      removePeer,
      setMuted,
      setModerator,
      setStaff,
      setPlus,
      showCaption,
      addMessage,
      peerReaction: (id, emojiChar, who, on) => applyReaction(messages, id, emojiChar, who || 'them', on, nameOfPeer),
      linkPreview: (id, card) => {
        applyLinkPreview(messages, id, card);
        // Pasting a YouTube link IS the gesture for "let us watch this". The
        // pin box lives in the video panel, which is easy to miss, so the
        // owner gets the action on the link itself.
        if (!roomVideo || !isRoomOwner || !card || !parseVideoUrl(card.url || '')) return;
        const node = messages.querySelector(`[data-mid="${cssEscapeId(id)}"] .link-card`);
        if (!node || node.parentElement.querySelector('.watch-together')) return;
        node.after(
          el('button', {
            class: 'btn btn-sm watch-together',
            text: t('Watch together'),
            title: t('Pin this for everyone in the room'),
            onClick: () => h.onVideoPin?.(card.url),
          }),
        );
      },
      peerUnsend: (id) => removeMessage(messages, id),
      addFile,
      addSystem,
      removeSystem: (id) => removeSystemNotice(id),
      setTyping,
      setVideo: (state) => roomVideo?.setState(state),
      /** The queue, in full (contract D7). `self` is who this screen is, so
       *  the panel can mark your own entries and know when the turn is yours. */
      setVideoQueue: (m, self) => roomVideo?.setQueue(m, self),
      /** ACTIVITY_STATE, in full: a new game builds a fresh frame. */
      setActivity: (m) => activityPanel.api.setState(m),
      /** ACTIVITY_EVENT, forwarded into the frame if it is for the game on screen. */
      activityEvent: (m) => activityPanel.api.event(m),
      setVideoOwner: (on, peerId) => {
        isRoomOwner = !!on;
        if (peerId !== undefined) ownerPeerId = peerId;
        roomVideo?.setOwner(on);
        // The owner starts activities in an ordinary room; in a room the site
        // keeps open, anyone does. The panel decides that for itself once it
        // knows which of the two you are.
        activityPanel.api.setOwner(on);
        stageBtn.classList.toggle('hidden', !(isRoomOwner && h.room.band === 'adult'));
        promoteBtn.classList.toggle(
          'hidden',
          !(isRoomOwner && h.room.band === 'adult' && !h.room.standing && !h.room.community && h.onPromote),
        );
        refreshRoster();
      },

      /** Who, if anyone, is currently here as a guest. */
      setStage: (m) => {
        stageUntil = m && m.guestId ? Number(m.until) || 0 : 0;
        stageWho = (m && m.handle) || 'Someone';
        stageIsMe = !!(m && m.isMe); // decided by app.js, which is where selfId lives
        refreshStageBanner();
      },
      showVote,
      updateVote,
      endVote,
      askPrivate,
      initEffects: (prefs) => media.api.initEffects(prefs),
      // A community's room is renamed by its host over HTTP; the new name
      // arrives in COMMUNITY_STATE and the header follows.
      setRoomName: (name) => {
        if (typeof name === 'string' && name.trim()) roomTitle.textContent = name.trim();
      },
      communitySlot,
      /**
       * Let the room go. The window listeners that keep the More menu pinned
       * are on `window`, not on anything inside `root`, so they outlive the
       * screen unless somebody takes them off -- and a person who walks in and
       * out of ten rooms would leave ten of them behind.
       */
      dispose() {
        window.removeEventListener('resize', remeasureMore);
        window.removeEventListener('scroll', remeasureMore, true);
        document.removeEventListener('keydown', onMoreKey);
        document.removeEventListener('click', onMoreClickAway);
      },
    },
  };
}
